// Group G7's words: scheduling (proposed slots, the free/busy grid, the time-zone line, conflicts, the itinerary) and
// documents (the drafted message, packet thumbnails, the document pane). Each entry quotes the line of his glossary or
// of the vendored design-system excerpts it is taken from; labels.test.mjs checks every quote, text and status.

import { defineLabels, template, type LabelDef, type LabelSource } from "../labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const satisfies LabelDef;

const SLOTS_WORDS = "*proposed slots* · *Conflict* · *OOF* · *time zone*";
const SLOT_EXT_WORDS = "*Conflict* · *OOF* · *working hours* · *room* · *time zone* · *Cannot schedule before Step 9 advancement*";
const AVAIL_WORDS = "*Attendees* · *free/busy* · *working hours* · *OOF* · *room* · *candidate availability*";
const ITIN_TERMS = "When (+ the *virtual / onsite / hybrid* `tag`) · Attendees · Room · Teams link";
/** Q-E15: the copy controls whose words sit outside his three sources wait for him. */
const COPY_CONTROLS = "controls that carry a word outside his three sources: *Open* · *Open profile* · *Print* · the copy icon on *Candidate itinerary* and *Teams link*";
const DOC_WORDS = "*Original resume* · *Resume versions*";
const DOC_TOOLS = "document preview well (`--bg-deep`, `--r-tile`, page stepper, zoom)";
const FILE_ERRORS = "Er three sentences (invalid · password-protected · unreadable)";
const FORMATS = "\"Coordinator schedules virtual/onsite/hybrid rounds, presentations, assessments and executive conversations.\"";
const KICKOFF = "\"Email includes app access, packet links, assigned ROOTS/skills, interview guidance, 24-hour scorecard SLA, confidentiality and debrief expectations.\"";

/** The scheduling words (DS #27, row 100, row 101, row 102, row 104; G §8, §9). */
export const SCHEDULING_LABELS = /* @__PURE__ */ defineLabels({
  proposedSlots: his("proposed slots"),
  conflict: his("Conflict"),
  oof: his("OOF"),
  workingHours: his("working hours"),
  room: his("room"),
  timeZone: his("time zone"),
  timeZoneField: his("Time zone"),
  attendees: his("Attendees"),
  freeBusy: his("free/busy"),
  free: his("free"),
  busy: his("busy"),
  candidateAvailability: his("candidate availability"),
  conflicts: his("Conflicts"),
  beforeStep9: his("Cannot schedule before Step 9 advancement."),
  panelDeclined: his(
    "Cannot schedule if required panel assignment is declined or coverage invalid."

),
  virtual: his("virtual"),
  onsite: his("onsite"),
  hybrid: his("hybrid"),
  teamsLink: pending("Teams link"),
  sameEvent: his("Updates modify the same Outlook event."),
});

/**
 * The itinerary's words (DS #49, #98): the grid reads *When · Attendees · Room · Teams link* (G §9 Attendees), the round's
 * virtual / onsite / hybrid a `tag` beside When, and the copy control is the *Candidate itinerary* button with the copy
 * icon (DS §3.4: V1's *Format · With · Where / link* heads and *Copy itinerary* are not drawn). The keys keep their
 * places: `with` names the attendees, `whereLink` the room and the Teams link a meeting holds.
 */
export const ITINERARY_LABELS = /* @__PURE__ */ defineLabels({
  candidateItinerary: pending("Candidate itinerary"),
  copyItinerary: pending("Candidate itinerary"),
  when: his("When"),
  with: his("Attendees"),
  whereLink: his("Room · Teams link"),
});

/** The documents' words (DS #50, #54, row 107, row 109; G §10). */
export const DOCUMENT_LABELS = /* @__PURE__ */ defineLabels({
  originalResume: his("Original resume"),
  resumeVersions: his("Resume versions"),
  pageStepper: his("page stepper"),
  zoom: his("zoom"),
  invalid: his("invalid"),
  passwordProtected: his("password-protected"),
  unreadable: his("unreadable"),
  pdfAndDocx: his("PDF and DOCX"),
  interviewPacket: his("Interview packet"),
  oneEmail: his("One released version creates one email."),
  kickoffIncludes: his(
    "Email includes app access, packet links, assigned ROOTS/skills, interview guidance, 24-hour scorecard SLA, confidentiality and debrief expectations."

),
});

/** Filled with numbers only: the stepper position (DS #70's *1 of n*), a version tag (DS row 118's *v1*), a count. */
export const G7_TEMPLATES = {
  pageOf: /* @__PURE__ */ template(his("{n} of {total}")),
  version: /* @__PURE__ */ template(his("v{n}")),
  conflictsCount: /* @__PURE__ */ template(his("Conflicts · {n}")),
} as const;
